import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { GreenroomState } from "../../stores/types/types";
import { Theme } from "../../stores/types/themes";
import classNames from "classnames";
import { GetThemeFonts } from "../../connection/themes";
import { KeyedFontAsset } from "../../stores/types/font-assets";
import useDynamicFonts, { getFontClassName } from "../../hooks/use-dynamic-fonts";

const ThemeOptionsContainer = styled.div`
    position: absolute;
    top: 44px;
    right: 32px;
    z-index: 1000;
    width: 200px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 12px;
    background: #26242B;
    opacity: 0;
    transition: opacity 0.2s ease-in;
    pointer-events: none;

    &.show {
        opacity: 1;
    }
`;

const ThemeOptionTypeHeader = styled.label`
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 16px;

    label {
        color: #807D8A !important;
        text-transform: uppercase;
    }
`;

const ColorwayContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
`;

const ColorSwatch = styled.div`
    display: flex;
    gap: 2px;
`;

const Color = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 2px;
    border: 0.5px solid rgba(54, 52, 60, 0.00);
`;

const FontContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
`;

export interface StylePackThemeOptionsProps {
    stylepack: string;
    showThemeOptions: boolean;
}

export default function StylePackThemeOptions(props: StylePackThemeOptionsProps) {
    const { stylepack, showThemeOptions } = props;
    const defaultThemes = useSelector((s:GreenroomState) => s.ThemesReducer.defaultThemes);
    const meetingId = useSelector((s:GreenroomState) => s.SessionReducer.meeting?.meeting);
    const fonts = useSelector((s:GreenroomState) => s.FontAssetsReducer.allFontAssets);

    const [themeFonts, setThemeFonts] = useState<KeyedFontAsset | null>(null);

    useDynamicFonts({ fonts });

    useEffect(() => {
        const fetchThemeFonts = async () => {
            // all default themes for a stylepack have the same fonts, so we can use the first one
            const firstThemeForStylepack = defaultThemes.filter((t:Theme) => t.stylepack === stylepack)[0];
            if (!firstThemeForStylepack) return;
            const fonts = await GetThemeFonts(meetingId ?? 0, firstThemeForStylepack.id);
            setThemeFonts(fonts);
        }
        fetchThemeFonts();
    }, [stylepack]);

    // first 3 colors of all themes
    const colorPalettes = useMemo(
        () => defaultThemes.filter((t:Theme) => t.stylepack === stylepack).map(
            (t) => ({...t, colors: t.colors.slice(0, 3)})), 
    [defaultThemes, stylepack])

    return (
        <ThemeOptionsContainer className={classNames({ show: showThemeOptions })}>
            <ThemeOptionTypeHeader>
                <label className="bl-font-l3">Fonts</label>
            </ThemeOptionTypeHeader>
            {themeFonts && Object.entries(themeFonts).map(([fieldKey, font]) => (
                <FontContainer key={fieldKey + font?.url}>
                    <span className={getFontClassName(font)}>
                        {font?.name}
                    </span>
                </FontContainer>
            ))}
            <ThemeOptionTypeHeader>
                <label className="bl-font-l3">Colorways</label>
            </ThemeOptionTypeHeader>
            {colorPalettes.map((palette) => (
                <ColorwayContainer key={palette.name}>
                    <label className="bl-font-p2">{palette.name}</label>
                    <ColorSwatch>
                        {palette.colors.map((color) => (
                            <Color style={{ backgroundColor: color }} key={color}/>
                        ))}
                    </ColorSwatch>
                </ColorwayContainer>
            ))}
        </ThemeOptionsContainer>
    );
}
